
import { Vue, Component, Prop, Watch } from 'vue-property-decorator';
import * as echarts from 'echarts';
import LineChartFilterable from '@/components/reports-v3/components/filterables/LineChartFilterable.vue';
import moment from 'moment';
import {getTableName, getSqlColumnRef, isComputedProperty, sqlExtractDay} from '@/worker/db/sqlHelpers';
import FilterWidget from '@/components/reports-v3/components/codedWidgets/shared/FilterWidget.vue';
import {PermissionsGroup} from '@/store/models.def';
import { GobiColor } from '@/helpers/color';
import gdbx from '@/store/modules/gdbx';
import IncludeTaxToggle from '@/components/IncludeTaxToggle.vue';
import ShowProfitToggle from '@/components/ShowProfitToggle.vue';
import TableCollapseToggle from '@/components/TableCollapseToggle.vue';
import settingx from '@/store/modules/settingx';
import { addComma } from '@/util/number';
import IncludeCreditNoteToggle from '@/components/IncludeCreditNoteToggle.vue';
import IncludeDebitNoteToggle from '@/components/IncludeDebitNoteToggle.vue';
import SlPhModuleOnlyToggle from '@/components/SlPhModuleOnlyToggle.vue';

interface PeriodTotalField {
    key: string;
    label: string;
    textAlign?: string;
}

interface PeriodTotalData {
    total_sales: string;
    total_sales_profit: string;
    total_credit_notes?: string;
    total_debit_notes?: string;
}

@Component({
	components: {
		IncludeCreditNoteToggle,
		IncludeDebitNoteToggle,
		IncludeTaxToggle,
		LineChartFilterable,
		ShowProfitToggle,
		SlPhModuleOnlyToggle,
		TableCollapseToggle,
	},
})

export default class DashboardCurrentMonthSalesLineChart extends FilterWidget {
    public get permissionIds(): PermissionsGroup[] {
        return ['dashboard'];
    }

	public filterIds: Array<
		| 'date'
		| 'dateAsOf'
		| 'dateRange'
		| 'stockItems'
		| 'agents'
		| 'customers'
		| 'suppliers'
	> = ['dateAsOf'];

	public xAxisData: string[] = [];
    public periodTotalData: PeriodTotalData[] = [];
    public periodTotalFields: PeriodTotalField[] = [];
	public series: echarts.ECharts[] | any = [];

	public get currencySymbol() {
		return gdbx.currencySymbol;
	}

	public get numDecimal() {
		return gdbx.numDecimal;
	}

	public get currentMonth(): [number, number] {
		const startMonth = moment(this.selectedAsOfDate).startOf('month').valueOf();
		const endMonth = moment(this.selectedAsOfDate).endOf('day').valueOf();
		return [startMonth, endMonth];
	}

	public get includeTax() {
		return settingx.now.includeTax;
	}

	public get showProfit() {
		return settingx.now.showProfit;
	}

    public get includeCreditNote() {
        return settingx.now.includeCreditNote;
    }

    public get includeDebitNote() {
        return settingx.now.includeDebitNote;
    }

	public get slPhModuleOnly() {
		return settingx.now.slPhModuleOnly;
	}

	public get color() {
		return this.showProfit
			? [GobiColor.COLOR1, GobiColor.COLOR2, GobiColor.COLOR3]
			: [GobiColor.COLOR1, GobiColor.COLOR3];
	}

	public get expensiveHook() {
		const {
			showProfit,
			includeTax,
			currentMonth,
			includeCreditNote,
			includeDebitNote,
			slPhModuleOnly,
		} = this;
		return JSON.stringify([
			showProfit,
			includeTax,
			currentMonth,
			includeCreditNote,
			includeDebitNote,
			slPhModuleOnly,
		]);
	}

    public async expensiveCalc() {
        this.generateLoadingText(0.2, 0.3);

        const amountKey = this.slPhModuleOnly
            ? (this.includeTax ? 'amountWithTax' : 'amount')
            : (this.includeTax ? 'arAmountWithTax' : 'arAmount');
        const profitKey = this.slPhModuleOnly
            ? (this.includeTax ? 'profitIncludeTax' : 'profit')
            : (this.includeTax ? 'arProfitIncludeTax' : 'arProfit');

        const amountCol = getSqlColumnRef('dashboardSales', amountKey);
        const amountExpr = `TRY_CAST(${amountCol} AS DOUBLE)`;
        const profitRef = getSqlColumnRef('dashboardSales', profitKey);
        const profitExpr = isComputedProperty(profitKey) ? profitRef : `TRY_CAST(${profitRef} AS DOUBLE)`;
        const table = getTableName('dashboardSales');

        const [monthStart, dayEnd] = this.currentMonth;
        const dayCount = moment(this.selectedAsOfDate).date();

        const jobId = this.currentJobId;
        const queryHead = await this._ensureTablesLoaded(['dashboardSales']);
        this.checkInterrupted(jobId);
        this.generateLoadingText(0.3, 0.5);

        const rows = await queryHead.rawQuery(`
            SELECT ${sqlExtractDay('"DOCDATE"', 'day_num')},
                SUM(CASE WHEN "DOCTYPE" IN ('IV','CS') THEN ${amountExpr} ELSE 0 END) as sales_amt,
                SUM(CASE WHEN "DOCTYPE" IN ('IV','CS') THEN ${profitExpr} ELSE 0 END) as sales_profit,
                SUM(CASE WHEN "DOCTYPE" = 'DN' THEN ${amountExpr} ELSE 0 END) as dn_amt,
                SUM(CASE WHEN "DOCTYPE" = 'DN' THEN ${profitExpr} ELSE 0 END) as dn_profit,
                SUM(CASE WHEN "DOCTYPE" = 'CN' THEN ${amountExpr} ELSE 0 END) as cn_amt,
                SUM(CASE WHEN "DOCTYPE" = 'CN' THEN ${profitExpr} ELSE 0 END) as cn_profit
            FROM ${table}
            WHERE TRY_CAST("DOCDATE" AS BIGINT) BETWEEN ${monthStart} AND ${dayEnd}
            GROUP BY day_num
            ORDER BY day_num
        `);
        this.checkInterrupted(jobId);
        this.generateLoadingText(0.9);

        const dayMap: Record<number, any> = {};
        for (const row of rows) {
            dayMap[Number(row.day_num)] = row;
        }

        const salesData: number[] = [];
        const profitData: number[] = [];
        const dnAmtData: number[] = [];
        const cnAmtData: number[] = [];
        const labels: string[] = [];
        let totalSales = 0;
        let totalProfit = 0;
        let totalDN = 0;
        let totalCN = 0;

        for (let d = 1; d <= dayCount; d++) {
            labels.push(String(d));
            const r = dayMap[d];
            const salesAmt = r ? Number(r.sales_amt) || 0 : 0;
            const salesPft = r ? Number(r.sales_profit) || 0 : 0;
            const dnAmt = r ? Number(r.dn_amt) || 0 : 0;
            const dnPft = r ? Number(r.dn_profit) || 0 : 0;
            const cnAmt = r ? Number(r.cn_amt) || 0 : 0;
            const cnPft = r ? Number(r.cn_profit) || 0 : 0;

            salesData.push(salesAmt);
            const dayPft = salesPft
                + (this.includeDebitNote ? dnPft : 0)
                - (this.includeCreditNote ? cnPft : 0);
            profitData.push(dayPft);
            dnAmtData.push(dnAmt);
            cnAmtData.push(cnAmt);

            totalSales += salesAmt;
            totalProfit += dayPft;
            totalDN += dnAmt;
            totalCN += cnAmt;
        }

        this.xAxisData = labels;

        this.periodTotalFields = [
            { key: 'total_sales', label: 'Total Sales', textAlign: 'center' },
            { key: 'total_debit_notes', label: 'Total Debit Notes', textAlign: 'center' },
            { key: 'total_credit_notes', label: 'Total Credit Notes', textAlign: 'center' },
            { key: 'total_sales_profit', label: 'Total Sales Profit', textAlign: 'center' },
        ];

        this.periodTotalData = [
            {
                total_sales: this.currencySymbol + ' ' + addComma(totalSales, this.numDecimal, true),
                total_sales_profit: this.currencySymbol + ' ' + addComma(totalProfit, this.numDecimal, true),
                total_debit_notes: this.currencySymbol + ' ' + addComma(
                    this.includeDebitNote ? totalDN : 0, this.numDecimal, true),
                total_credit_notes: this.currencySymbol + ' ' + addComma(
                    this.includeCreditNote ? totalCN : 0, this.numDecimal, true),
            },
        ];

        const sales = {
            name: 'Sales',
            data: salesData,
            type: 'line',
            areaStyle: { color: GobiColor.COLOR1, opacity: 1 },
            smooth: true,
        };

        const profit = {
            name: 'Profit / Loss',
            data: profitData,
            type: 'line',
            areaStyle: { color: GobiColor.COLOR2, opacity: 1 },
            smooth: true,
        };

        const debitNotes = {
            name: 'Debit Notes',
            data: dnAmtData,
            type: 'line',
            color: GobiColor.COLOR3,
            smooth: true,
        };

        const creditNotes = {
            name: 'Credit Notes',
            data: cnAmtData,
            type: 'line',
            color: GobiColor.COLOR4,
            smooth: true,
        };

        this.series = [sales];

        if (this.includeDebitNote) {
            this.series.push(debitNotes);
        }

        if (this.includeCreditNote) {
            this.series.push(creditNotes);
        }

        if (this.showProfit) {
            this.series.push(profit);
        }

        this.generateLoadingText(1);
        this.saveHistory('series', 'xAxisData', 'periodTotalData', 'periodTotalFields');
    }
}
